import React from "react";
import { motion } from "framer-motion";
import { MousePointerClickIcon } from "lucide-react";
import { BRAND } from "../lib/data";

/**
 * ClickHint — wraps an interactive element and adds:
 *  - a soft pulsing brand-accent ring so users can tell what's clickable
 *  - a "tap/press" scale animation on click + hover
 *  - a DISTINCTIVE floating cursor badge that stands apart from brand UI
 *
 * The badge is intentionally a loud, non-brand attention color (electric lime)
 * with a diamond/tag shape + pointer notch, so it can never be mistaken for a
 * real product element during a demo.
 */

// Distinctive, non-brand demo-affordance color (electric lime).
const HINT_COLOR = "#C6FF00";
const HINT_INK = "#1A2E05";
type ClickHintProps = {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  label?: string;
  /** Show the little floating cursor badge. Defaults to true. */
  badge?: boolean;
  /** Rounding of the pulsing ring so it hugs the child. */
  rounded?: string;
  ariaLabel?: string;
};
export function ClickHint({
  children,
  onClick,
  className = "",
  label,
  badge = true,
  rounded = "rounded-2xl",
  ariaLabel
}: ClickHintProps) {
  return <motion.button type="button" onClick={onClick} aria-label={ariaLabel || label} className={`group/hint relative block text-left ${className}`} whileHover={{
    scale: 1.01
  }} whileTap={{
    scale: 0.97
  }} transition={{
    type: "spring",
    stiffness: 400,
    damping: 22
  }} data-id="element-170" data-mp="37qcdwms838jx">
      {/* Pulsing brand-accent ring */}
      <motion.span aria-hidden="true" className={`pointer-events-none absolute -inset-1 ${rounded}`} style={{
      boxShadow: `0 0 0 0 ${BRAND.accent}59`
    }} animate={{
      boxShadow: [`0 0 0 0 ${BRAND.accent}59`, `0 0 0 6px ${BRAND.accent}00`]
    }} transition={{
      duration: 1.8,
      repeat: Infinity,
      ease: "easeOut"
    }} data-id="element-171" data-mp="2tt5v4s7i19up">
        <span className={`absolute inset-0 ${rounded}`} style={{
        boxShadow: `inset 0 0 0 2px ${BRAND.accent}8C`
      }} data-id="element-172" data-mp="hzpg3uyoqooo" />
      </motion.span>

      {/* Distinctive floating cursor badge — electric lime diamond-tag */}
      {badge && <motion.span aria-hidden="true" className="pointer-events-none absolute -right-2.5 -top-2.5 z-20" animate={{
      y: [0, -3, 0]
    }} transition={{
      duration: 1.4,
      repeat: Infinity,
      ease: "easeInOut"
    }} data-id="element-173" data-mp="22kvw18kakxd8">
          <span className="relative flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-extrabold uppercase tracking-wide shadow-lg" style={{
        backgroundColor: HINT_COLOR,
        color: HINT_INK,
        boxShadow: "0 4px 12px rgba(0,0,0,0.28), 0 0 0 2px #ffffff"
      }} data-id="element-174" data-mp="307bby5x2lmyo">
            <MousePointerClickIcon className="h-3.5 w-3.5" strokeWidth={2.75} data-id="element-175" data-mp="1aqtugttilsur" />
            {label && <span className="pr-0.5" data-id="element-176" data-mp="1eurur7ukt9pe">{label}</span>}
            {/* Pointer notch */}
            <span className="absolute -bottom-1 left-3 h-2.5 w-2.5 rotate-45" style={{
          backgroundColor: HINT_COLOR
        }} data-id="element-177" data-mp="179o7gk97uj5r" />
          </span>
        </motion.span>}

      {children}
    </motion.button>;
}